import React, { useState, useRef, useEffect, useMemo } from "react";

const FieldManagerPanel = ({
  fields, // now all fields, not just one page
  selectedFieldId,
  selectField,
  removeField,
  updateFieldName,
  scrollFieldIntoView,
}) => {
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [expandedPages, setExpandedPages] = useState({});
  const inputRefs = useRef({});

  // Focus input when entering edit mode
  useEffect(() => {
    if (editingFieldId !== null) {
      inputRefs.current[editingFieldId]?.focus();
    }
  }, [editingFieldId]);

  // Build a sorted list of pages and group fields
  const { pages, groupedFields } = useMemo(() => {
    const byPage = fields.reduce((acc, f) => {
      (acc[f.page] = acc[f.page] || []).push(f);
      return acc;
    }, {});
    const pg = Object.keys(byPage)
      .map(Number)
      .sort((a, b) => a - b);
    return { pages: pg, groupedFields: byPage };
  }, [fields]);

  // Toggle expand/collapse per page
  const togglePage = (page) => {
    setExpandedPages((prev) => ({
      ...prev,
      [page]: !prev[page],
    }));
  };

  return (
    <div className="p-2">
      {pages.map((page) => {
        const isOpen = expandedPages[page] ?? true;
        const pageFields = groupedFields[page];

        return (
          <div key={page} className="mb-2">
            {/* Page header */}
            <button
              onClick={() => togglePage(page)}
              className="w-full flex justify-between items-center text-sm text-tier-primary bg-gray-200 px-3 py-1 rounded-md"
            >
              <span>Page {page}</span>
              <span>
                ({pageFields.length}) {isOpen ? "▾" : "▸"}
              </span>
            </button>

            {/* Fields list */}
            {isOpen && (
              <div className="mt-2 space-y-2 pl-4">
                {pageFields.map((field) => {
                  const isEditing = editingFieldId === field.id;
                  const isSelected = selectedFieldId === field.id;
                  const selectedCls = isSelected
                    ? "bg-tier-secondary text-white"
                    : "text-gray-800";

                  // pick icon
                  let iconName = "text_fields";
                  if (field.type === "checkbox") iconName = "check_box";
                  if (field.type === "image") iconName = "image";

                  return (
                    <div
                      key={field.id}
                      className={`flex items-center space-x-2 p-1 rounded-lg cursor-pointer ${selectedCls}`}
                      onClick={() => {
                        selectField(field.id);
                        scrollFieldIntoView(field.id);
                      }}
                    >
                      {/* Icon or thumbnail */}
                      {field.type === "image" || field.type === "signature" ? (
                        <img
                          src={field.src}
                          alt={field.name || ""}
                          className="w-5 h-5 object-cover rounded"
                        />
                      ) : (
                        <span className="material-symbols-outlined text-base">
                          {iconName}
                        </span>
                      )}

                      {/* Name / Alt text */}
                      <input
                        ref={(el) => (inputRefs.current[field.id] = el)}
                        type="text"
                        value={field.name || ""}
                        onChange={(e) =>
                          updateFieldName(field.id, e.target.value)
                        }
                        readOnly={!isEditing}
                        placeholder={
                          field.type === "image"
                            ? "Enter alt text"
                            : "Enter field name"
                        }
                        onClick={(e) => {
                          if (!isEditing) {
                            e.stopPropagation();
                            selectField(field.id);
                          }
                        }}
                        className={`flex-1 border p-1 text-sm rounded outline-none ${
                          !isEditing
                            ? "bg-transparent cursor-pointer"
                            : "text-tier-primary"
                        }`}
                      />

                      {/* Edit toggle */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingFieldId(isEditing ? null : field.id);
                        }}
                        className="text-tier-primary hover:text-blue-700"
                        title={isEditing ? "Finish editing" : "Edit Field"}
                      >
                        <span className="material-symbols-outlined text-sm">
                          {isEditing ? "check" : "edit"}
                        </span>
                      </button>

                      {/* Delete */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeField(field.id);
                        }}
                        className="text-tier-primary hover:text-red-700"
                        title="Delete Field"
                      >
                        <span className="material-symbols-outlined text-sm">
                          delete
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default FieldManagerPanel;
